import type { Ref } from 'vue';
import type { BookingProduct, StopNumber } from '@/models/advancedJobBooking';

import { ref } from 'vue';

import { createEmptyBookingProduct } from '@/models/advancedJobBooking';

export interface JobBookingProductsComposable {
  products: Ref<BookingProduct[]>;
  addProduct: (fromStop: StopNumber, toStop: StopNumber) => void;
  addFinisher: (fromStop: StopNumber, toStop: StopNumber) => void;
  removeProduct: (index: number) => void;
  productsForPair: (
    fromStop: StopNumber,
    toStop: StopNumber,
  ) => { product: BookingProduct; index: number }[];
  removeProductsForRemovedStops: (totalStops: number) => void;
  setProducts: (next: BookingProduct[]) => void;
  reset: () => void;
}

export default function useJobBookingProducts(): JobBookingProductsComposable {
  const products = ref<BookingProduct[]>([createEmptyBookingProduct(1, 2)]);

  function addProduct(fromStop: StopNumber, toStop: StopNumber): void {
    products.value.push(createEmptyBookingProduct(fromStop, toStop));
  }

  function addFinisher(fromStop: StopNumber, toStop: StopNumber): void {
    const last = products.value
      .filter((p) => p.from_stop === fromStop && p.to_stop === toStop)
      .at(-1);
    products.value.push({
      ...createEmptyBookingProduct(fromStop, toStop),
      finisher: true,
      installation: last?.installation ?? '',
      returns: last?.returns ?? '',
    });
  }

  function removeProduct(index: number): void {
    products.value.splice(index, 1);
  }

  function productsForPair(
    fromStop: StopNumber,
    toStop: StopNumber,
  ): { product: BookingProduct; index: number }[] {
    return products.value
      .map((product, index) => ({ product, index }))
      .filter(
        ({ product }) =>
          product.from_stop === fromStop && product.to_stop === toStop,
      );
  }

  function removeProductsForRemovedStops(totalStops: number): void {
    products.value = products.value.filter(
      (p) => p.from_stop <= totalStops && p.to_stop <= totalStops,
    );
  }

  function setProducts(next: BookingProduct[]): void {
    products.value = next;
  }

  function reset(): void {
    products.value = [createEmptyBookingProduct(1, 2)];
  }

  return {
    products,
    addProduct,
    addFinisher,
    removeProduct,
    productsForPair,
    removeProductsForRemovedStops,
    setProducts,
    reset,
  };
}
